import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { App } from '@/app/App'
import { I18nProvider } from '@/app/providers/I18nProvider'
import { QueryProvider } from '@/app/providers/QueryProvider'
import { SeoProvider } from '@/app/providers/SeoProvider'
import { ChatStreamProvider } from '@/features/chat-stream/context/ChatStreamContext'
import { readConsentFromStorage } from '@/features/consent/store/consentStore'
import { AppErrorBoundary } from '@/shared/components/AppErrorBoundary'
import { initClarity } from '@/shared/lib/clarity'
import { flushClientEvents } from '@/shared/lib/clientEvents'
import { installNativeErrorReporting } from '@/shared/lib/errorReporting'
import { initMetaPixel } from '@/shared/lib/metaPixel'
import { initNewRelic } from '@/shared/lib/newrelic'
import { detectPrerenderedSnapshot } from '@/shared/lib/prerender'
import { reloadOnceForStaleChunk } from '@/shared/lib/staleChunk'
import { removeShellHeadTags } from '@/shared/seo/shellHead'

// Fonts self-hosted (audit SEO 2026-06-04, Sprint 2 subtarea 4):
// reemplaza los 4 archivos de Google Fonts del antiguo index.html por
// solo 2 familias servidas desde el bundle propio:
//   - Bricolage Grotesque (display/headings, hero) — variable font para
//     soportar opsz + todos los pesos sin Pedir archivos extra.
//   - Space Mono (chips, etiquetas brutal, marquee, fechas) — solo 400 y 700.
// Body cae a system-ui (cero red, look nativo). font-mono colapsa también
// a Space Mono (uso muy puntual: DebugCrons + SessionDateTimePicker).
import '@fontsource-variable/bricolage-grotesque'
import '@fontsource/space-mono/400.css'
import '@fontsource/space-mono/700.css'

import './styles/globals.css'

/**
 * MSW en browser solo si VITE_USE_MSW=true. Por defecto OFF (incluso en dev)
 * para que el frontend hable con el backend real.
 *
 * Los tests usan MSW vía Node ({@code src/mocks/server.ts}) y no se ven
 * afectados por esta flag.
 *
 * Si arrastras un service worker registrado de antes, deberás desregistrarlo
 * una vez: DevTools → Application → Service Workers → Unregister.
 */
async function enableMocking(): Promise<void> {
  if (import.meta.env.VITE_USE_MSW !== 'true') return
  const { worker } = await import('@/mocks/browser')
  await worker.start({ onUnhandledRequest: 'bypass' })
}

// Inicializa New Relic Browser Agent — gated por consent RGPD (sprint
// 2026-06-16). Solo arranca si el user dio opt-in explícito para cookies
// analíticas. Si nunca decidió o rechazó, NR queda inactivo y el banner
// (montado por App.tsx) le pide la decisión. Cuando acepta, el siguiente
// render lo arrancará — ver useEffect en App.tsx.
const consent = readConsentFromStorage()
if (consent?.analytics === true) {
  initNewRelic()
  initClarity()
}
// Píxel de Meta — categoría "publicidad", independiente de analíticas.
if (consent?.marketing === true) {
  initMetaPixel()
}

// Chunk de una versión anterior (tras un despliegue): Vite avisa antes de que falle el
// import de la ruta. Recargamos una vez para bajar la versión nueva; si ya lo hicimos
// hace nada, dejamos que el error llegue al `errorElement` de la ruta raíz.
// App nativa: errores no controlados a nuestro backend (las apps no llevan New Relic).
installNativeErrorReporting()

window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForStaleChunk()) event.preventDefault()
})

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root not found')

removeShellHeadTags()
detectPrerenderedSnapshot(rootElement)

void enableMocking().then(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <AppErrorBoundary>
        <SeoProvider>
          <I18nProvider>
            <QueryProvider>
              <ChatStreamProvider>
                <App />
              </ChatStreamProvider>
            </QueryProvider>
          </I18nProvider>
        </SeoProvider>
      </AppErrorBoundary>
    </StrictMode>,
  )
  // Incidencias que quedaron en cola (p. ej. un atasco que acabó recargando la app).
  void flushClientEvents()
})
